<template>
  <div class="card shadow-sm table-card mb-4">
    <div class="form-card-header flex-wrap">
      <div class="section-number"><i class="fe fe-refresh-cw" style="font-size: 12px;"></i></div>
      <div class="flex-grow-1">
        <h6 class="mb-0 font-weight-bold text-white">Renewals and attrition</h6>
        <small style="color: rgba(255,255,255,.75);">Principals whose cover ran out each month, how many renewed, how many lapsed ({{ grace }} days' grace)</small>
      </div>
      <router-link to="/call-centre" class="btn btn-sm btn-light"><i class="fe fe-phone-call mr-1"></i> Call centre</router-link>
    </div>
    <div class="card-body">
      <div class="d-flex flex-wrap align-items-center mb-3" style="gap: 8px;">
        <PeriodPicker v-model="period" @change="load" />
        <select class="form-control form-control-sm" style="width: auto;" v-model="sector" @change="load" aria-label="Programme">
          <option value="">All programmes</option>
          <option v-for="s in sectorOptions" :key="s" :value="s">{{ s }}</option>
        </select>
      </div>
      <div v-if="loading && !data" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
      <template v-else-if="data">
        <div class="row">
          <div class="col-lg-3 col-md-6" v-for="tile in tiles" :key="tile.label">
            <div class="stat-card">
              <div class="stat-icon-wrap" :style="{ background: tile.bg }"><i :class="tile.icon" :style="{ color: tile.color }"></i></div>
              <div><p class="stat-label">{{ tile.label }}</p><h3 class="stat-value">{{ tile.value }}</h3><p class="stat-sub">{{ tile.sub }}</p></div>
            </div>
          </div>
        </div>
        <apexchart type="line" height="320" :options="options" :series="series" :key="chartKey" />

        <div class="due-box mt-3">
          <div class="d-flex flex-wrap align-items-center" style="gap: 12px;">
            <div><b>Due for renewal:</b> {{ up.next_30_days.toLocaleString() }} in 30 days · {{ up.next_60_days.toLocaleString() }} in 60 · {{ up.next_90_days.toLocaleString() }} in 90 · {{ up.expired_last_30_days.toLocaleString() }} expired in the last 30 days</div>
            <button v-if="canPush" class="btn btn-sm btn-submit ml-auto" @click="showPush = !showPush"><i class="fe fe-phone-forwarded mr-1"></i> Send to call centre</button>
          </div>
          <div v-if="showPush" class="push-form mt-3">
            <div class="form-row">
              <div class="form-group col-md-3">
                <label class="small font-weight-bold text-secondary">Cover ending within</label>
                <select class="form-control form-control-sm" v-model.number="push.days"><option :value="7">7 days</option><option :value="30">30 days</option><option :value="60">60 days</option><option :value="90">90 days</option></select>
              </div>
              <div class="form-group col-md-3">
                <label class="small font-weight-bold text-secondary">Include expired in the last</label>
                <select class="form-control form-control-sm" v-model.number="push.expired_days"><option :value="0">None</option><option :value="30">30 days</option><option :value="90">90 days</option></select>
              </div>
              <div class="form-group col-md-4">
                <label class="small font-weight-bold text-secondary">Programme</label>
                <select class="form-control form-control-sm" v-model="push.sector">
                  <option value="">All (voluntary contributors first)</option>
                  <option v-for="s in sectorOptions" :key="s" :value="s">{{ s }}</option>
                </select>
              </div>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <button class="btn btn-sm btn-outline-primary" @click="preview">Preview list</button>
              <button class="btn btn-sm btn-submit" @click="send" :disabled="sending">{{ sending ? "Sending…" : "Send to call centre" }}</button>
              <small v-if="previewCount !== null" class="text-muted">{{ previewCount }} enrollee(s) match.</small>
            </div>
            <div v-if="previewRows.length" class="table-responsive mt-2 preview-table">
              <table class="table table-sm mb-0">
                <thead class="table-head"><tr><th class="table-th">Enrollee</th><th class="table-th">Programme</th><th class="table-th">Phone</th><th class="table-th">Cover ends</th><th class="table-th">Facility</th></tr></thead>
                <tbody>
                  <tr class="table-row" v-for="r in previewRows" :key="r.id">
                    <td><router-link :to="`/client/${r.id}`">{{ r.firstname }} {{ r.lastname }}</router-link><div class="small text-muted">{{ r.id_card_number }}</div></td>
                    <td>{{ r.sector }}</td><td>{{ r.phone_number || "—" }}</td><td>{{ String(r.expiry_date).substr(0, 10) }}</td><td>{{ r.facility || "—" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import PeriodPicker from "./PeriodPicker.vue";

/** Renewal / attrition per month (GET analytics/renewals) and pushing due enrollees to the call centre. */
export default {
  name: "RenewalsPanel",
  components: { PeriodPicker },
  data() {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    const title = String(user.job_title || "").trim().toLowerCase();
    return {
      period: { from: "", to: "" },
      sector: "",
      sectorOptions: [],
      data: null,
      loading: false,
      chartKey: 0,
      canPush: user.type === "shis" || String(user.user_role) === "1" || ["executive secretary", "director", "call centre", "call center", "customer care", "customer service"].includes(title),
      showPush: false,
      push: { days: 30, expired_days: 0, sector: "Voluntary Contributor" },
      previewRows: [],
      previewCount: null,
      sending: false,
    };
  },
  computed: {
    grace() { return this.data ? this.data.summary.grace_days : 30; },
    up() { return this.data.upcoming; },
    tiles() {
      const s = this.data.summary;
      return [
        { label: "Renewal rate", value: s.renewal_rate === null ? "—" : s.renewal_rate + "%", sub: `${s.renewed.toLocaleString()} of ${s.due.toLocaleString()} covers due (ended months)`, icon: "fe fe-refresh-cw", bg: "#ecfdf5", color: "#059669" },
        { label: "Attrition rate", value: s.attrition_rate === null ? "—" : s.attrition_rate + "%", sub: `still lapsed ${s.grace_days} days after expiry`, icon: "fe fe-user-x", bg: "#fef2f2", color: "#dc2626" },
        { label: "Renewals recorded", value: s.renewals_made.toLocaleString(), sub: "in the period", icon: "fe fe-check-circle", bg: "#eff6ff", color: "#2563eb" },
        { label: "Due in 30 days", value: this.up.next_30_days.toLocaleString(), sub: `${this.up.expired_last_30_days.toLocaleString()} expired in the last 30 days`, icon: "fe fe-clock", bg: "#fff7ed", color: "#ea580c" },
      ];
    },
    series() {
      const m = this.data.months;
      return [
        { name: "Covers due", type: "column", data: m.map((r) => r.due) },
        { name: "Renewed", type: "column", data: m.map((r) => r.renewed) },
        { name: "Renewal rate %", type: "line", data: m.map((r) => r.renewal_rate) },
        { name: "Attrition rate %", type: "line", data: m.map((r) => r.attrition_rate) },
      ];
    },
    options() {
      return {
        chart: { toolbar: { show: true } },
        colors: ["#cbd5e1", "#059669", "#2563eb", "#dc2626"],
        stroke: { width: [0, 0, 3, 3], curve: "smooth" },
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
        xaxis: { categories: this.data.months.map((r) => r.label + (r.in_progress ? " (so far)" : "")) },
        yaxis: [
          { seriesName: "Covers due", title: { text: "Principals" }, labels: { formatter: (v) => Math.round(v).toLocaleString() } },
          { seriesName: "Covers due", show: false },
          { seriesName: "Renewal rate %", opposite: true, min: 0, max: 100, title: { text: "%" } },
          { seriesName: "Renewal rate %", show: false, min: 0, max: 100 },
        ],
        dataLabels: { enabled: true },
        legend: { position: "top" },
        tooltip: { shared: true, intersect: false },
      };
    },
  },
  mounted() {
    this.$batchGet("/api/v1/auth/analytics/enrollment/sectors").then((res) => {
      this.sectorOptions = (res.data.data.rows || []).map((r) => r.sector);
    }).catch(() => {});
    if (this.period.from) this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.$batchGet("/api/v1/auth/analytics/renewals", { params: { from: this.period.from, to: this.period.to, sector: this.sector || undefined } })
        .then((res) => { this.data = res.data.data; this.chartKey++; })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
    preview() {
      this.axios.get("/api/v1/auth/call-centre/due", { params: { ...this.push, sector: this.push.sector || undefined, limit: 50 } })
        .then((res) => { this.previewRows = res.data.data; this.previewCount = res.data.count; });
    },
    send() {
      this.sending = true;
      this.axios.post("/api/v1/auth/call-centre/push", { ...this.push, sector: this.push.sector || null })
        .then((res) => this.$toasted.success(res.data.message, { position: "top-center", duration: 4000 }))
        .catch((e) => this.$toasted.error((e.response && e.response.data && e.response.data.message) || "Could not send.", { position: "top-center", duration: 4000 }))
        .finally(() => { this.sending = false; });
    },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
.due-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; font-size: 0.88rem; }
.push-form { border-top: 1px dashed #cbd5e1; padding-top: 12px; }
.preview-table { max-height: 260px; overflow-y: auto; }
</style>
